import { useEffect, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError, useApi } from "../api";
import { Button, ButtonLink } from "./Button";
import { Field, Input } from "./Field";
import { ErrorBox, Loading } from "./Misc";
import { TenantChooser, clearLoginHint, readLoginHint } from "./TenantChooser";
import { LogoTile } from "./Layout";

interface Providers { tenant: { slug: string; displayName: string }; localLoginEnabled: boolean; entraRequired?: boolean; providers: { id: string; kind: string; displayName: string; isDefault: boolean }[] }

/** Tenant login page: customer IdPs (OIDC/SAML) first, local login as fallback. Page tinted 6 % with the brand. */
export function LoginPage({ title, returnTo = "/", logoUrl, poweredBy }: { title?: string; returnTo?: string; logoUrl?: string | null; poweredBy?: string }) {
  const qc = useQueryClient();
  // No retry on mount: an app shell may already have fetched this (same key) and got the 404 that means "no tenant
  // here"; refetching it only risks a cancelled fetch (StrictMode) that leaves the query pending and idle.
  const { data, error: providersError, isLoading } = useApi<Providers>(["auth-providers"], "/api/auth/providers", undefined,
    { retryOnMount: false, retry: (n, err) => err.status >= 500 && n < 1 });
  // Pre-filled from the start page, which found this tenant from the e-mail domain; used once.
  const [email, setEmail] = useState(readLoginHint);
  useEffect(clearLoginHint, []);
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // With Entra sign-in required the password form is for break-glass accounts only and starts folded away.
  const [breakGlass, setBreakGlass] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await apiFetch("/api/auth/login", { method: "POST", json: { email, password, totp: totp || undefined } });
      await qc.invalidateQueries({ queryKey: ["me"] });
      window.location.href = returnTo;
    } catch (err) {
      if (err instanceof ApiError && err.code === "totp_required") setNeedTotp(true);
      else setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally { setBusy(false); }
  }

  if (isLoading) return <Loading />;
  if (providersError instanceof ApiError && providersError.status === 404) return <TenantChooser />;
  const name = data?.tenant.displayName ?? "";
  return (
    <div className="flex min-h-full items-center justify-center bg-brand-tint p-6">
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-dialog bg-surface p-7 shadow-e3">
        <LogoTile name={name} logoUrl={logoUrl} size={40} />
        <h1 className="text-h2">{title ?? (name ? `Sign in to ${name}` : "Sign in")}</h1>
        {data?.providers.map((p) => (
          <ButtonLink key={p.id} variant="primary" size="touch" href={`${p.kind === "entra" ? "/api/auth/entra" : `/api/auth/oidc/${p.id}`}/start?returnTo=${encodeURIComponent(returnTo)}${p.kind === "entra" && email ? `&login_hint=${encodeURIComponent(email)}` : ""}`}>Continue with {p.displayName}</ButtonLink>
        ))}
        {data?.entraRequired && !breakGlass && <button type="button" className="self-start text-small text-fg-3 underline" onClick={() => setBreakGlass(true)}>Emergency sign-in with a password</button>}
        {(data?.localLoginEnabled ?? true) && (!data?.entraRequired || breakGlass) && (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <Field label="E-mail"><Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus={!email} /></Field>
            <Field label="Password"><Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus={!!email} /></Field>
            {needTotp && <Field label="One-time code"><Input inputMode="numeric" autoComplete="one-time-code" value={totp} onChange={(e) => setTotp(e.target.value)} autoFocus /></Field>}
            <ErrorBox error={error} />
            <Button type="submit" variant={data?.providers.length ? "secondary" : "primary"} size="touch" loading={busy}>{data?.entraRequired ? "Emergency sign-in" : data?.providers.length ? "Use email instead" : "Sign in"}</Button>
          </form>
        )}
        {poweredBy && <p className="text-xs text-fg-3">Powered by {poweredBy}</p>}
      </div>
    </div>
  );
}
